import React, { useEffect, useMemo } from "react";

import config from "@/environment";
import { useCachedQuery, useValueCache } from "@components/hooks";
import { LoadingSlide } from "@components/title-slide-view";
import { NewOverviewExperienceSwitch } from "@features/overview-v2/components/top-bars/configuration-menu/new-overview-experience-switch";
import { useFeatureFlags } from "@hooks/http";
import { logEvent } from "@internal/analytics";
import {
  todayLocalAsISODate,
  type CategoricalBreakdownConfig,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { AccrualPerformance } from "./accrual-performance";
import { CashSnapshot } from "./cash-snapshot";
import { useAccrualPerformance } from "./hooks/use-accrual-performance";
import { useCashSnapshot } from "./hooks/use-cash-snapshot";
import {
  ReportBodyControl,
  useReportBodyState,
  type ReportBodyState,
} from "./report-body-control";

export interface FetchReportParams<ResponseType> {
  response: ResponseType;
  isLoading: boolean;
  bodyState: ReportBodyState;
  breakdown: string;
}

interface OverviewProps {
  breakdownConfigs: CategoricalBreakdownConfig[];
  reportEventName: string;
  onLoadingStatusChange?: (isLoading: boolean) => void;
}

export function Overview({
  breakdownConfigs,
  reportEventName,
  onLoadingStatusChange,
}: OverviewProps) {
  const {
    state,
    setFilters,
    setAttributionModel,
    setDateRange,
    setWindowSizeDays,
    setBreakdown,
  } = useReportBodyState(breakdownConfigs);
  const {
    dateRange: dr,
    windowSizeDays,
    attributionModel,
    breakdown,
    ...bodyRest
  } = state;
  const anchor = todayLocalAsISODate();
  const dateRange = parseDateRangeArgument(dr, anchor);

  const variables = useValueCache({
    dateRange,
    attributionModel,
    breakdown,
    windowSizeDays: Number(windowSizeDays),
    ...bodyRest,
  });
  const { snapshotLoading, snapshotData: _snapshotData } =
    useCashSnapshot(variables);

  const { performanceLoading, performanceData: _performanceData } =
    useAccrualPerformance(variables);

  const { hasFeatureFlag } = useFeatureFlags();
  const overviewPageV2 = hasFeatureFlag("overviewPageV2");

  const isLoading = useMemo(
    () => snapshotLoading || performanceLoading,
    [snapshotLoading, performanceLoading],
  );

  useEffect(() => {
    onLoadingStatusChange?.(isLoading);
  }, [isLoading, onLoadingStatusChange]);

  useEffect(() => {
    if (isLoading) {
      logEvent(reportEventName, {
        Breakdown: breakdown,
        Filters: state.filters.map((v) => v.key),
        AttributionModel: attributionModel,
      });
    }
  }, [reportEventName, isLoading, breakdown, state.filters, attributionModel]);

  const snapshotData = useCachedQuery(_snapshotData);
  const performanceData = useCachedQuery(_performanceData);

  if (!snapshotData?.me?.overviewPage || !performanceData?.me?.overviewPage) {
    return <LoadingSlide />;
  }

  return (
    <>
      <ReportBodyControl
        isLoading={isLoading}
        state={state}
        breakdownConfigs={breakdownConfigs}
        setFilters={setFilters}
        setAttributionModel={setAttributionModel}
        setDateRange={setDateRange}
        setWindowSizeDays={setWindowSizeDays}
        setBreakdown={setBreakdown}
      />
      <div className="px-4">
        {overviewPageV2 && (
          <div className="float-right mt-1 py-4">
            <NewOverviewExperienceSwitch />
          </div>
        )}
        <CashSnapshot
          isLoading={snapshotLoading}
          response={snapshotData.me.overviewPage}
          bodyState={state}
          breakdown={breakdown}
        />
        <AccrualPerformance
          isLoading={performanceLoading}
          response={performanceData.me.overviewPage}
          bodyState={state}
          breakdown={breakdown}
        />
      </div>
    </>
  );
}
